import { CarSearch } from "@models/car";
import { buildSpatieSort } from "../shared/utils/buildSpatieSort";
import { normalizeCustomDataFilter } from "../shared/utils/normalizeCustomDataFilter";

export type CarInclude = "customData" | "customer" | "customer.customData";

export type CarSortField =
  | "id"
  | "license_number"
  | "account_number"
  | "info"
  | "manufacturer"
  | "model"
  | "production_year"
  | "created_at"
  | "updated_at";

type CarCustomDataSortField = `custom_data.${number}.field_value`;
type CarCustomDataSort = CarCustomDataSortField | `-${CarCustomDataSortField}`;
export type CarSort = CarSortField | `-${CarSortField}` | CarCustomDataSort;

const CAR_SORT_FIELDS: CarSortField[] = [
  "id",
  "license_number",
  "account_number",
  "info",
  "manufacturer",
  "model",
  "production_year",
  "created_at",
  "updated_at",
];
const CAR_CUSTOM_DATA_SORT_PATTERN = /^custom_data\.\d+\.field_value$/;

export const CAR_BROWSE_INCLUDES: CarInclude[] = ["customData", "customer.customData"];
export const CAR_BROWSE_PER_PAGE = 10;

export interface CarSearchParams {
  filter?: Partial<CarSearch>;
  include?: CarInclude[];
  sort?: CarSort[];
  per_page?: number;
  cursor?: string;
  useCache?: boolean;
}

/**
 * Strips empty custom_data entries and normalizes custom_data filter shape for car search.
 * @param filter CarSearch - Raw car search filter from browse UI.
 * @returns CarSearch - Filter safe to send to the cars search endpoint.
 */
export function normalizeCarSearchFilter(filter: CarSearch): CarSearch {

  // Map custom_data array into API-expected search filter shape
  const custom_data = normalizeCustomDataFilter(filter.custom_data);
  const { custom_data: _drop, ...rest } = filter;

  return {
    ...rest,
    ...(custom_data !== undefined && { custom_data }),
  };
}

/**
 * Builds the POST body for the cars Spatie browse/search endpoint.
 * @param params CarSearchParams - Browse filters, includes, sort, and pagination.
 * @returns object - Request body fields for cars/search.
 */
export function getCarSearchBody(params: CarSearchParams) {

  const { filter: rawFilter, include, sort, per_page = CAR_BROWSE_PER_PAGE, cursor, useCache } = params;

  // Normalize filter fields (e.g. custom data) for Spatie search API
  const filter = rawFilter ? normalizeCarSearchFilter(rawFilter) : undefined;

  return {
    ...(filter && Object.keys(filter).length > 0 && { filter }),
    ...(include?.length && { include: include.join(",") }),
    ...(sort?.length && { sort: sort.join(",") }),
    ...(per_page && { per_page }),
    ...(cursor && { cursor }),
    ...(useCache && { use_cache: true }),
  };
}

/**
 * Builds a Spatie sort string for car browse from column key and order.
 * @param sortKey string - Column field name from the browse table.
 * @param order "ASC" | "DESC" - Sort direction from the browse table.
 * @returns CarSort - Spatie sort token accepted by the cars search API.
 */
export function buildCarSort(sortKey: string, order: "ASC" | "DESC"): CarSort {

  return buildSpatieSort(sortKey, order, CAR_SORT_FIELDS, "id", CAR_CUSTOM_DATA_SORT_PATTERN) as CarSort;
}
